import type { MouseEvent as ReactMouseEvent, KeyboardEvent } from 'react';
import type { FindingValueSource } from '@/models/report';
import { useState, useCallback, useRef, useEffect } from 'react';
import { navigateWorksheetTab } from '../../../shared/utils/tabNavigation';
import styles from './StatementRow.module.css';

/** Badge text per source. Only generated statements are badged: a reader's own
 *  wording needs no label. Whether EMv2-graded (ECHOMEASURE_V2) statements
 *  get a badge too is still open.
 *  Keep these short — the field's text is indented by a fixed amount to clear
 *  the badge (see .inputBadged). */
const SOURCE_BADGES: Partial<Record<FindingValueSource, string>> = {
  CARDIOVISION_V2: 'CVv2',
};

interface StatementRowProps {
  text: string;
  valueSource?: FindingValueSource | null;
  index: number;
  onSave: (index: number, newText: string) => void;
  onDelete: (index: number) => void;
  disabled?: boolean;
}

/**
 * One statement line. "Editing" is nothing more than the textarea having
 * focus: there is no stored edit flag, so two rows can never both look
 * focused. The buffer commits on blur and follows the committed text whenever
 * it changes from outside (undo / redo), so a later blur can't re-save a
 * stale edit.
 */
export default function StatementRow({
  text,
  valueSource,
  index,
  onSave,
  onDelete,
  disabled = false,
}: StatementRowProps) {
  const [editValue, setEditValue] = useState(text);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => setEditValue(text), [text]);

  // Auto-resize to fit content without scrollbars
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [editValue]);

  const handleBlur = useCallback(() => {
    const trimmed = editValue.trim();
    if (!trimmed) {
      onDelete(index);
    } else {
      onSave(index, trimmed);
    }
  }, [editValue, index, onDelete, onSave]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        navigateWorksheetTab(textareaRef.current!, e.shiftKey);
        return;
      }
      if (e.key === 'Backspace' && editValue === '') {
        e.preventDefault();
        const container = textareaRef.current?.closest('[data-finding-input]');
        const focusables = Array.from(
          container?.querySelectorAll<HTMLElement>('textarea, input') ?? []
        );
        const idx = focusables.indexOf(textareaRef.current!);
        const prevEl = idx > 0 ? focusables[idx - 1] : null;
        onDelete(index);
        if (prevEl) setTimeout(() => prevEl.focus(), 0);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        textareaRef.current?.blur();
      } else if (e.key === 'Escape') {
        textareaRef.current?.blur();
      }
    },
    [editValue, index, onDelete]
  );

  const handleDeleteClick = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation();
      onDelete(index);
    },
    [index, onDelete]
  );

  const badge = valueSource ? SOURCE_BADGES[valueSource] : undefined;

  return (
    <div className={styles.row}>
      <textarea
        ref={textareaRef}
        className={`${styles.input} ${disabled ? styles.inputDisabled : ''} ${badge ? styles.inputBadged : ''}`}
        value={editValue}
        data-committed={text}
        readOnly={disabled}
        rows={1}
        onFocus={disabled ? undefined : (e) => e.target.select()}
        onChange={disabled ? undefined : (e) => setEditValue(e.target.value)}
        onBlur={disabled ? undefined : handleBlur}
        onKeyDown={disabled ? undefined : handleKeyDown}
      />
      {badge && (
        <span
          className={styles.sourceBadge}
          title={`Generated from ${valueSource}`}
        >
          {badge}
        </span>
      )}
      {!disabled && (
        <button
          type="button"
          tabIndex={-1}
          className={styles.deleteButton}
          onClick={handleDeleteClick}
          aria-label="Remove statement"
        >
          &times;
        </button>
      )}
    </div>
  );
}
